import {Domain, HandleReturnType, StateToMessagesReturnType} from '../../domain.js';
import {BusIncomingType, CommandType, DomainType, MessageType, NodeMessage, SendMessageAction} from '../../node.js';
import {DomainInfo} from '../../state/types/domain-registry.js';
import {DomainKeys, Peer, State} from '../../state/types/state.js';
import {getLogger} from '../../logger.js';
import {broadcast, error, isLocalSource, isRemoteSource, success, unicast} from '../../common/messages.js';
import {join as gJoin} from '../global/messages.js'
import {exceptionToReason, reason} from '../../reason.js';
import {busDomainUri, busUnhandledMessage, failure} from './constants.js';
import {inDomain, joinDomain, leaveDomain} from '../../state/core.js';
import {Subscription, SubscriptionId} from '../../state/types/bus.js';
import {DomainJoin, RemoteJoin} from '../global/types/messages.js';
import {
    byId,
    byIdInDomain,
    byIdInDomainThrows,
    bySourceInDomain,
    isClusterPeer,
    JoinedPeer,
    peersInDomain,
    setPeer,
    visiblePeers
} from '../../state/peers.js';
import {exceptionData} from '../../common/types.js';
import {parse} from '../../restrictions.js';
import {peerToAddress} from '../../address.js';
import {nodeId, requestId} from '../../common/ids.js';
import {PublishRequest, SubscribeRequest, SubscriptionData, UnsubscribeRequest} from './types/requests.js';
import {Draft, produce} from 'immer';
import {event, subscribed} from './messages.js';
import {Identity, NodeAddress, OriginType, PeerId, Restrictions, Source} from '../../state/types/common.js';
import {Publish, Subscribe, Unsubscribe} from './types/messages.js';
import {IOGateway} from '../../../gateway';

const log = getLogger('gateway.domains.bus')

// joining and leaving the domain

/**
 * Remote peer is joining the domain.
 *
 *   Updates the state and generates messages to all existing peers announcing the new peer
 */
function remoteJoin(state: State, source: Source, request: {peer_id: PeerId, restrictions: Restrictions}): HandleReturnType {
    const {peer_id: peerId, restrictions} = request;
    if (!inDomain(state, peerId, DomainKeys.Bus)) {
        return [joinDomain(state, peerId, DomainKeys.Bus, restrictions), []];
    }
    return [state, []];
}

/*Local peer is joining the domain.

  Updates the state and generates messages to all existing peers announcing the new peer*/
function localJoin(state: State, source: Source, request: DomainJoin, options?: BusDomainOptions): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, restrictions} = request;
    if (inDomain(state, peerId, DomainKeys.Bus)) {
        // the peer is already in the domain
        return [state, [success(busDomainUri, source, requestId, peerId)]];
    }
    // we need to join it

    const parsedRestrictions = parse(restrictions) ?? options?.defaultPeerRestrictions;
    state = joinDomain(state, peerId, DomainKeys.Bus, parsedRestrictions);
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Bus);

    const message = {...request, type: 'join', options: peer?.options, restrictions: peer[DomainKeys.Bus].restrictions} as unknown as RemoteJoin;
    return [state, [
        success(busDomainUri, source, requestId, peerId),
        broadcast(peerToAddress(nodeId(state.ids), peerId), message)
    ]];
}

function forwardJoin(state: State, source: Source, request) {
    return isRemoteSource(source) ? remoteJoin(state, source, request) : localJoin(state, source, request);
}


/**
 * Source has disconnected from the system
 */
function sourceRemoved(state: State, source: Source): HandleReturnType {
    if (log.enabledFor('debug')) {
        log.debug(`removing source ${JSON.stringify(source)} from bus domain`);
    }
    state = bySourceInDomain(state, source, DomainKeys.Bus).reduce((s, p) => {
        return leaveDomain(s, p.id, DomainKeys.Bus);
    }, state);
    return [state, []];
}


function remoteLeave(state: State, source: Source, request): HandleReturnType {
    const {peer_id: peerId} = request;
    const peer = byId(state, peerId);
    if (peer) {
        return [leaveDomain(state, peerId, DomainKeys.Bus), []];
    }
    return [state, []];
}

function localLeave(state: State, source: Source, request): HandleReturnType {
    const {request_id: requestId, peer_id: peerId} = request;
    const newState = leaveDomain(state, peerId, DomainKeys.Bus);
    return [newState, [success(busDomainUri, source, requestId, peerId), broadcast(peerToAddress(nodeId(state.ids), peerId), {
        ...request,
        type: 'leave'
    })]];
}

function forwardLeave(state: State, source: Source, request): HandleReturnType {
    return isRemoteSource(source) ? remoteLeave(state, source, request) : localLeave(state, source, request);
}

// event publishing

/**
 * given a collection of peers, returns a [[peer subscription-a] [peer subscription-b]] where
 *   subscription-a and subscription-b are the topic subscriptions of the peer
 */
function zipPeerSubscriptions(peers: Peer[]): Array<{ peer: Peer, subscription: [SubscriptionId, Subscription] }> {
    return peers.map((peer: Peer) => {
        const result: [Peer, Array<[SubscriptionId, Subscription]>] = [peer, Object.entries<Subscription>(peer[DomainKeys.Bus]?.subscriptions || {})];
        return result;
    }).flatMap(([peer, entries]: [Peer, Array<[SubscriptionId, Subscription]>]) => {
        return entries
            .map(([id, subscription]: [SubscriptionId, Subscription]) => {
                return {peer, subscription: [id, subscription]};
            });
    });
}

/**
 * checks if sub-topic matches topic
 */
function matchesTopic({
                          topic: subTopic,
                          topicRepattern: subPattern
                      }: { topic: string, topicRepattern?: RegExp }, topic: string): boolean {

    if (subPattern) {
        const match = topic.match(subPattern);
        return match !== null && topic === match[0];
    } else {
        return subTopic === topic;
    }
}

function matchesRouting(subKey?: string, key?: string): boolean {
    return !subKey || !key || subKey === key;
}

/**
 * checks if given topic and routing key match an existing subscription
 */
function matchesSubscription(topic: string, routingKey: string | undefined, [, subscription]: [SubscriptionId, Subscription]): boolean {
    return matchesTopic(subscription, topic) && matchesRouting(subscription.routingKey, routingKey);
}

function matchesTarget(target: Identity | undefined, peer: Peer): boolean {
    if (target) {
        const peerIdentity = peer.identity;
        for (const [k, v] of Object.entries(target)) {
            if (peerIdentity[k] !== v) {
                return false;
            }
        }
    }
    return true;
}

/**
 * distributes a message onto all matching subscribers
 */
function publish(state: State, request: PublishRequest, publisher: BusPeer): HandleReturnType {
    const {data} = request;
    if (data) {
        const {topic, routing_key: routingKey, peer_id: publisherId, target_identity: target} = request;
        const matcher: (entry: [SubscriptionId, Subscription]) => boolean = (entry) => matchesSubscription(topic, routingKey, entry);
        const targetMatcher: (peer: Peer) => boolean = (peer) => matchesTarget(target, peer);
        const publisherIdentity = publisher.identity;
        const remotes = new Set<string>();
        const messages: SendMessageAction<MessageType>[] =
            zipPeerSubscriptions(
                visiblePeers(state, DomainKeys.Bus, publisher, true)
                    .filter(targetMatcher)
            ).filter(({subscription}) => matcher(subscription))
                .reduce((messages: SendMessageAction<MessageType>[], {
                    peer: subscriber,
                    subscription
                }: { peer: Peer, subscription: [SubscriptionId, Subscription] }) => {
                    const subSource = subscriber.source;
                    const subscriberId = subscriber.id;
                    const [first, ] = subscription;
                    const msg = isLocalSource(subSource)
                        ? event(subSource, subscriberId, first, publisherIdentity, data)
                        : function (): SendMessageAction<'publish'> | undefined {
                            // remote source - we need to propagate the request to a unique list of nodes
                            // otherwise there will be event duplication

                            if (publisherId !== subscriberId) {
                                const node = subSource['node'];
                                if (remotes.has(node)) {
                                    remotes.add(node);
                                    const recipient: NodeAddress = {
                                        type: 'node',
                                        node: node
                                    };
                                    return unicast(peerToAddress(nodeId(state.ids), publisherId), recipient, request as Publish);
                                }
                            }
                        }();
                    return msg ? messages.concat(msg) : messages;
                }, []);
        return [state, messages];
    }
    return [state, []];
}

function localPublish(state: State, request: PublishRequest): HandleReturnType {
    const {peer_id: publisherId} = request;
    const peer = byIdInDomainThrows(state, publisherId, DomainKeys.Bus);
    if (peer) {
        return publish(state, request, peer);
    }
    return [state, []];
}

function remotePublish(state: State, request: PublishRequest): HandleReturnType {
    const {peer_id: publisherId} = request;
    const peer = byIdInDomain(state, publisherId, DomainKeys.Bus);
    if (peer) {
        return publish(state, request, peer);
    }
    return [state, []];
}

export function forwardPublish(state: State, source: Source, request): HandleReturnType {
    return isLocalSource(source) ? localPublish(state, request) : remotePublish(state, request);
}

function isWildcard(topic: string): boolean {
    return topic.indexOf('>') !== -1 || topic.indexOf('*') !== -1;
}

function topicToPattern(topic: string): RegExp {
    return new RegExp(
        topic
            .replace(/\./g, '\\.')
            .replace(/\*/g, '[a-zA-Z_0-9]+')
            .replace(/>/g, '.*'), 'g');
}

type BusPeer = JoinedPeer<DomainKeys.Bus>

function subscribe(state: State, request: SubscribeRequest, subscriptionId: SubscriptionId, peer: BusPeer): HandleReturnType {
    const {topic, routing_key: routingKey, request_id: requestId, peer_id: peerId} = request;
    const source = peer.source;
    peer = produce(peer, (draft: Draft<BusPeer>) => {
        draft[DomainKeys.Bus].subscriptions = draft[DomainKeys.Bus].subscriptions || {};
        draft[DomainKeys.Bus].subscriptions[subscriptionId] = {topic, routingKey};
        if (isWildcard(topic)) {
            draft[DomainKeys.Bus].subscriptions[subscriptionId].topicRepattern = topicToPattern(topic);
        }
    });
    const message: BroadcastSubscribe = {...request as Subscribe, ...{subscription_id: subscriptionId}};
    return [setPeer(state, peerId, peer), isLocalSource(source) ? [
        subscribed(source, requestId, peerId, subscriptionId),
        broadcast(peerToAddress(nodeId(state.ids), peerId), message)
    ] : []]
}

type BroadcastSubscribe = Subscribe & SubscriptionData;

function localSubscribe(state: State, source: Source, request: SubscribeRequest): HandleReturnType {
    const [newIds, subscriptionId] = requestId(state.ids);
    const peerId = request.peer_id;
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Bus);
    return subscribe({...state, ids: newIds}, request, subscriptionId, peer);
}

function remoteSubscribe(state: State, source: Source, request: BroadcastSubscribe): HandleReturnType {
    const {subscription_id: subscriptionId, peer_id: peerId} = request;
    const peer = byIdInDomain(state, peerId, DomainKeys.Bus);
    if (peer) {
        return subscribe(state, request, subscriptionId, peer);
    }
    return [state, []];
}

export function forwardSubscribe(state: State, source: Source, request: SubscribeRequest | BroadcastSubscribe): HandleReturnType {
    return isLocalSource(source) ? localSubscribe(state, source, request) : remoteSubscribe(state, source, request as BroadcastSubscribe);
}

function unsubscribe(state: State, request: UnsubscribeRequest, peer: BusPeer): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, subscription_id: subscriptionId} = request;
    const source = peer.source;
    peer = produce(peer, (draft: Draft<BusPeer>) => {
        if (draft[DomainKeys.Bus].subscriptions) {
            delete draft[DomainKeys.Bus].subscriptions[subscriptionId];
        }
    });
    return [setPeer(state, peerId, peer), isLocalSource(source) ? [
        success(busDomainUri, source, requestId, peerId),
        broadcast(peerToAddress(nodeId(state.ids), peerId), request as Unsubscribe)
    ] : []];
}

function localUnsubscribe(state: State, source: Source, request: UnsubscribeRequest): HandleReturnType {
    const peer = byIdInDomainThrows(state, request.peer_id, DomainKeys.Bus);
    return unsubscribe(state, request, peer);
}

function remoteUnsubscribe(state: State, request): HandleReturnType {
    const peer = byIdInDomain(state, request.peer_id, DomainKeys.Bus);
    if (peer) {
        return unsubscribe(state, request, peer);
    }
    return [state, []];
}

export function forwardUnsubscribe(state: State, source: Source, request: UnsubscribeRequest): HandleReturnType {
    return isLocalSource(source) ? localUnsubscribe(state, source, request) : remoteUnsubscribe(state, request);
}


function handleRequest(state: State, source: Source, request, options?: BusDomainOptions): HandleReturnType {

    switch (request.type) {
        case 'domain/join': {
            return forwardJoin(state, source, request as DomainJoin);
        }
        case 'domain/leave': {
            return forwardLeave(state, source, request);
        }
        case 'commands/source-removed': {
            return sourceRemoved(state, source);
        }
        case 'publish': {
            return forwardPublish(state, source, request);
        }
        case 'subscribe': {
            return forwardSubscribe(state, source, request);
        }
        case 'unsubscribe': {
            return forwardUnsubscribe(state, source, request);
        }
        default: {
            log.error(`Unhandled message ${JSON.stringify(request)}`);
            return [state, [error(busDomainUri, source, request['request_id'] ?? -1, request['peer_id'], reason(busUnhandledMessage, `Unhandled message ${JSON.stringify(request)}`))]];
        }
    }
}

class BusDomain implements Domain<BusIncomingType> {
    constructor(private readonly options?: BusDomainOptions) {}
    info(): DomainInfo {
        return {
            uri: busDomainUri,
            description: '',
            version: 1
        };
    }

    init(state: State): State {
        return state;
    }

    destroy(state: State): State {
        return state;
    }

    handleMessage(state: State, msg: NodeMessage<CommandType | DomainType | BusIncomingType>): HandleReturnType {
        const {source, body} = msg;
        try {
            return handleRequest(state, source, body, this.options);
        } catch (e) {
            if (!exceptionData(e)) {
                log.error(`Error processing message ${JSON.stringify(msg)}`, e);
            }
            return [state, [error(busDomainUri, source, body['request_id'], body['peer_id'], exceptionToReason(e, failure))]];
        }
    }

    stateToMessages(state: State): StateToMessagesReturnType {
        const node = nodeId(state.ids);
        const receiver = undefined as unknown as Source;
        const result = peersInDomain(state, DomainKeys.Bus)
            .filter(peer => isLocalSource(peer.source) && isClusterPeer(DomainKeys.Bus, peer))
            .flatMap(peer => {
                const peerId = peer.id;
                const messages: IOGateway.Message<'join'>[]= [
                    gJoin(undefined, peerId, peer[DomainKeys.Bus].restrictions, busDomainUri, peer.identity, peer.options),
                ];
                return messages.map(msg => unicast(peerToAddress(node, peerId), receiver, msg));
            });

        return result;
    }

}

export type BusDomainOptions = {
    defaultPeerRestrictions?: OriginType;
}
export function busDomain(options?: BusDomainOptions): BusDomain {
    return new BusDomain(options);
}
